import type { Locale } from "./i18n";

export type DocTagTone = "model" | "metric" | "state" | "risk" | "ui" | "data";

export type DocTermId =
  | "regime"
  | "regime-analysis"
  | "hmm"
  | "momentum"
  | "volatility"
  | "ewma-volatility"
  | "drawdown"
  | "transition-matrix"
  | "correlation"
  | "sharpe-ratio"
  | "dividend-yield"
  | "payout-ratio"
  | "sustainability-score"
  | "cover-ratio"
  | "cash-ratio"
  | "per"
  | "pbr"
  | "roe"
  | "roic"
  | "dupont-analysis"
  | "financial-leverage"
  | "debt-equity-ratio"
  | "expected-return"
  | "portfolio-dividend-yield"
  | "effective-holdings"
  | "top3-concentration"
  | "var"
  | "cvar"
  | "crisis-scenario"
  | "diversification-score"
  | "return-improvement"
  | "risk-improvement";

type LocalizedText = Record<Locale, string>;

type LocalizedDocTermDefinition = {
  id: DocTermId;
  tone: DocTagTone;
  label: LocalizedText;
  tag: LocalizedText;
  summary: LocalizedText;
  detail?: Partial<LocalizedText>;
  meaning: LocalizedText;
};

type DocTermDefinition = {
  id: DocTermId;
  tone: DocTagTone;
  label: string;
  tag: string;
  summary: string;
  detail?: string;
  meaning: string;
};

export const DOC_TERM_DEFINITIONS: Record<DocTermId, LocalizedDocTermDefinition> = {
  regime: {
    id: "regime",
    tone: "state",
    label: { ja: "レジーム", en: "Regime" },
    tag: { ja: "状態", en: "State" },
    summary: {
      ja: "相場の向き、荒さ、下げの深さをまとめた現在の状態ラベルです。",
      en: "A market-state label combining trend, turbulence, and downside depth.",
    },
    detail: {
      ja: "Velpha ではモメンタム、ボラティリティ、ドローダウン、HMM の状態を重ねて、上昇・安定や持ち直しのような読みやすい言葉へ変換しています。",
      en: "Velpha combines momentum, volatility, drawdown, and HMM state into labels such as stable uptrend or rebound.",
    },
    meaning: {
      ja: "その時点の相場がどんな局面にあるかを表す言葉です。",
      en: "A label for the market's current phase.",
    },
  },
  "regime-analysis": {
    id: "regime-analysis",
    tone: "state",
    label: { ja: "レジーム分析", en: "Regime Analysis" },
    tag: { ja: "状態", en: "State" },
    summary: {
      ja: "相場をいくつかの局面に分けて読む考え方です。",
      en: "A framework for reading markets through interpretable states.",
    },
    detail: {
      ja: "価格の向きだけでなく、荒さや高値からの下がり幅まで合わせて、今が追い風か、加熱か、持ち直しかを整理します。",
      en: "It combines direction, turbulence, and distance from the peak to separate supportive, overheated, and rebound conditions.",
    },
    meaning: {
      ja: "値動きを上がる・下がるだけで終わらせず、局面として読む見方です。",
      en: "A way to interpret price movement as market phases rather than just up or down.",
    },
  },
  hmm: {
    id: "hmm",
    tone: "model",
    label: { ja: "HMM", en: "HMM" },
    tag: { ja: "モデル", en: "Model" },
    summary: {
      ja: "Hidden Markov Model の略で、見えない状態を確率的に推定するモデルです。",
      en: "A Hidden Markov Model that probabilistically infers latent states.",
    },
    detail: {
      ja: "Velpha では日次リターンと最近の荒さを材料に、表面には見えない相場の潜在状態を推定し、日々のノイズを少しならして局面のまとまりをつかみます。",
      en: "Velpha uses daily returns and recent turbulence to infer latent market states and smooth out noisy day-to-day movement.",
    },
    meaning: {
      ja: "相場の裏にある見えにくい状態を、確率で推定するためのモデルです。",
      en: "A probabilistic model for inferring hidden market states.",
    },
  },
  momentum: {
    id: "momentum",
    tone: "metric",
    label: { ja: "モメンタム", en: "Momentum" },
    tag: { ja: "指標", en: "Metric" },
    summary: {
      ja: "一定期間で価格がどちらへ、どれだけ動いたかを見る指標です。",
      en: "A metric for how far and in which direction price moved over a window.",
    },
    detail: {
      ja: "Velpha のレジーム分析では主に 63 日モメンタムを使い、短すぎるノイズを避けながら直近 3 か月の向きをつかみます。",
      en: "Velpha mainly uses 63-day momentum to capture three-month direction without overreacting to short noise.",
    },
    meaning: {
      ja: "最近の値動きが上向きか下向きか、勢いがあるかを見る入口の指標です。",
      en: "A signal for recent direction and strength.",
    },
  },
  volatility: {
    id: "volatility",
    tone: "metric",
    label: { ja: "ボラティリティ", en: "Volatility" },
    tag: { ja: "指標", en: "Metric" },
    summary: {
      ja: "値動きの大きさや荒さを表す指標です。",
      en: "A metric describing the size and turbulence of price moves.",
    },
    detail: {
      ja: "同じ上昇でも、ボラティリティが低ければ持ちやすい上昇、高ければ乱高下を含む不安定な上昇として読みます。",
      en: "The same uptrend can be calm and easier to hold or unstable and choppy depending on volatility.",
    },
    meaning: {
      ja: "どれくらい大きく上下しやすいかを表す指標で、持ちやすさや不安定さの判断に直結します。",
      en: "A measure of how turbulent price movement is.",
    },
  },
  "ewma-volatility": {
    id: "ewma-volatility",
    tone: "metric",
    label: { ja: "EWMA ボラティリティ", en: "EWMA Volatility" },
    tag: { ja: "指標", en: "Metric" },
    summary: {
      ja: "直近の値動きに少し重みを置いて計算するボラティリティです。",
      en: "A volatility estimate that puts extra weight on more recent moves.",
    },
    detail: {
      ja: "単純な 21 日ボラティリティより、直近のショックを早めに反映しやすいのが特徴です。急に荒れ始めた局面を、通常の平均より早くつかむために使います。",
      en: "It reacts faster than a simple rolling volatility and helps detect freshly emerging turbulence.",
    },
    meaning: {
      ja: "最近の大きな動きをやや強めに反映して、相場の荒れ始めを早くつかむためのボラティリティです。",
      en: "A volatility measure that reacts faster to recent shocks.",
    },
  },
  drawdown: {
    id: "drawdown",
    tone: "risk",
    label: { ja: "ドローダウン", en: "Drawdown" },
    tag: { ja: "リスク", en: "Risk" },
    summary: {
      ja: "過去の高値からどれだけ下がっているかを示す指標です。",
      en: "A metric for how far price is below its previous peak.",
    },
    detail: {
      ja: "0% に近ければ高値圏、マイナスが深いほどまだ下げの影響が残っている状態と読みます。反発していても、この数字が深いままなら完全回復とは言いにくくなります。",
      en: "Near 0% means close to the peak; deeper negatives imply that downside impact still remains.",
    },
    meaning: {
      ja: "高値からの下がり幅を通じて、いまどれだけ深い下げを引きずっているかを見る指標です。",
      en: "A measure of how far price remains below its peak.",
    },
  },
  "transition-matrix": {
    id: "transition-matrix",
    tone: "model",
    label: { ja: "遷移行列", en: "Transition Matrix" },
    tag: { ja: "モデル", en: "Model" },
    summary: {
      ja: "ある状態から次の状態へ移る確率を並べた表です。",
      en: "A matrix of probabilities for moving from one state to another.",
    },
    detail: {
      ja: "見たいのは「どれが必ず当たるか」ではなく、「今の状態は続きやすいのか、別の状態へ広がりやすいのか」です。Velpha ではモデル遷移と観測遷移を混ぜて将来分布を出します。",
      en: "It is used to judge whether the current state is sticky or likely to spread into other states, and Velpha blends model and observed transitions.",
    },
    meaning: {
      ja: "今の状態が次にどこへ移りやすいかを確率で確認するための表です。",
      en: "A tool to inspect where the current state is likely to move next.",
    },
  },
  correlation: {
    id: "correlation",
    tone: "data",
    label: { ja: "相関", en: "Correlation" },
    tag: { ja: "データ", en: "Data" },
    summary: {
      ja: "2つの資産がどれくらい同じ方向に動くかを表します。",
      en: "Shows how strongly two assets tend to move in the same direction.",
    },
    detail: {
      ja: "銘柄数が多くても相関が高いと、悪い局面でまとめて下がりやすく、分散効果は見た目ほど出ません。分散の裏どりとして最後に確認したい指標です。",
      en: "High correlation limits real diversification even when the number of holdings looks large.",
    },
    meaning: {
      ja: "分散が本当に効いているかを確かめるために、銘柄同士の動きの似やすさを見る指標です。",
      en: "A relationship used to judge whether diversification is truly effective.",
    },
  },
  "sharpe-ratio": {
    id: "sharpe-ratio",
    tone: "metric",
    label: { ja: "シャープレシオ", en: "Sharpe Ratio" },
    tag: { ja: "指標", en: "Metric" },
    summary: {
      ja: "リスクに対してどれだけ効率よくリターンを取れているかを見る指標です。",
      en: "A metric for return efficiency relative to risk.",
    },
    detail: {
      ja: "同じリスクならより高いリターン、同じリターンならより低いリスクを評価したいときに使います。高リターンでも揺れが大きすぎる組み合わせを見分ける補助線になります。",
      en: "It helps compare portfolios by preferring more return for the same risk or less risk for the same return.",
    },
    meaning: {
      ja: "伸びと揺れをまとめて、リスクに見合った効率のよさを確認する指標です。",
      en: "A single efficiency score balancing growth and variability.",
    },
  },
  "dividend-yield": {
    id: "dividend-yield",
    tone: "metric",
    label: { ja: "配当利回り", en: "Dividend Yield" },
    tag: { ja: "指標", en: "Metric" },
    summary: {
      ja: "今の株価に対して、年間配当がどれくらいあるかを示す数字です。",
      en: "A metric showing annual dividends relative to the current stock price.",
    },
    detail: {
      ja: "見た目の魅力は分かりやすいですが、株価が下がった結果として高く見えているだけのこともあります。だから入口にはなっても、単独では結論を出しません。",
      en: "It can look attractive, but it may simply be high because the stock price has fallen.",
    },
    meaning: {
      ja: "配当の見た目の魅力を見る最初の数字ですが、その高さに無理がないかは別の指標で確認する必要があります。",
      en: "An entry-point metric for dividend attractiveness.",
    },
  },
  "payout-ratio": {
    id: "payout-ratio",
    tone: "metric",
    label: { ja: "配当性向", en: "Payout Ratio" },
    tag: { ja: "指標", en: "Metric" },
    summary: {
      ja: "利益の何割を配当に回しているかを示す数字です。",
      en: "A metric showing what share of profit is being paid out as dividends.",
    },
    detail: {
      ja: "高すぎると利益以上に配っている可能性があり、今の利回りが高く見えても将来まで続くとは読みづらくなります。配当分析の最初の土台です。",
      en: "If it is too high, the company may be over-distributing relative to profits.",
    },
    meaning: {
      ja: "利益の範囲内で無理なく配れているかを確かめる、配当分析の土台になる指標です。",
      en: "A core metric for judging whether dividends are supported by profit.",
    },
  },
  "sustainability-score": {
    id: "sustainability-score",
    tone: "metric",
    label: { ja: "持続可能性スコア", en: "Sustainability Score" },
    tag: { ja: "指標", en: "Metric" },
    summary: {
      ja: "配当の続けやすさを、複数の指標をまとめて見た総合評価です。",
      en: "A combined score for dividend sustainability across several metrics.",
    },
    detail: {
      ja: "配当性向、現金のカバー力、履歴の安定性などを1つの点数へ集約します。ただし平均点だけでなく、その点数を押し下げている弱点を一緒に見る必要があります。",
      en: "It aggregates payout ratio, cash coverage, and history, but weak points still need to be checked directly.",
    },
    meaning: {
      ja: "配当の土台をひと目でつかむための総合点ですが、弱い項目を見落とさないために内訳も一緒に読むべき指標です。",
      en: "A composite score for dividend sustainability that still requires checking its weak components.",
    },
  },
  "cover-ratio": {
    id: "cover-ratio",
    tone: "metric",
    label: { ja: "カバー力", en: "Coverage Ratio" },
    tag: { ja: "指標", en: "Metric" },
    summary: {
      ja: "営業キャッシュフローで配当と必要な投資をどこまで支えられるかを見る数字です。",
      en: "A metric for how well operating cash flow covers dividends and required investment.",
    },
    detail: {
      ja: "1 を下回ると、本業の現金だけでは配当と投資をまかなえていない状態です。利益が出ていても現金が弱い会社を見分けるために使います。",
      en: "Below 1 means dividends and investment are not fully covered by operating cash flow.",
    },
    meaning: {
      ja: "配当が会計上の利益ではなく現金で支えられているかを確かめるための指標です。",
      en: "A check on whether dividends are backed by actual cash generation.",
    },
  },
  "cash-ratio": {
    id: "cash-ratio",
    tone: "risk",
    label: { ja: "キャッシュ比率", en: "Cash Ratio" },
    tag: { ja: "リスク", en: "Risk" },
    summary: {
      ja: "短期の債務に対して、どれだけ現金余力があるかを見る数字です。",
      en: "A metric for cash available relative to short-term liabilities.",
    },
    detail: {
      ja: "低いと、少し業績が崩れただけでも配当維持や運転資金の面で苦しくなりやすくなります。利益より先に資金繰りが問題になる会社を見分ける補助線です。",
      en: "A low value can signal that even a modest deterioration may pressure liquidity and dividends.",
    },
    meaning: {
      ja: "現金面の余裕がどれだけあるかを見て、少し悪化したときの耐久力を確かめる指標です。",
      en: "A liquidity gauge for short-term financial resilience.",
    },
  },
  per: {
    id: "per",
    tone: "metric",
    label: { ja: "PER", en: "PER" },
    tag: { ja: "指標", en: "Metric" },
    summary: {
      ja: "利益に対して株価が何倍かを表す代表的なバリュエーション指標です。",
      en: "A valuation multiple comparing stock price to earnings.",
    },
    detail: {
      ja: "同業よりかなり低ければ割安に見えますが、先行き不安や利益の弱さを市場が織り込んでいるだけのこともあります。見たいのは安さそのものより、なぜ安いかです。",
      en: "A low PER can mean cheap, but it can also reflect weak prospects or fragile earnings.",
    },
    meaning: {
      ja: "割安感を見る入口の指標ですが、低い理由を ROE や ROIC、財務と合わせて切り分ける必要があります。",
      en: "An entry-point valuation metric that needs context from profitability and balance-sheet quality.",
    },
  },
  pbr: {
    id: "pbr",
    tone: "metric",
    label: { ja: "PBR", en: "PBR" },
    tag: { ja: "指標", en: "Metric" },
    summary: {
      ja: "純資産に対して株価が何倍かを表す指標です。",
      en: "A valuation multiple comparing stock price to book value.",
    },
    detail: {
      ja: "1 倍割れは目立ちますが、それだけでお得とは言えません。資本効率や収益性が弱いままなら、安さには理由がある可能性があります。",
      en: "Trading below book can be notable, but low capital efficiency may explain it.",
    },
    meaning: {
      ja: "見た目の割安感を測る代表的な指標ですが、低い理由まで掘って読む必要があります。",
      en: "A common valuation metric that still needs explanation for why it is low or high.",
    },
  },
  roe: {
    id: "roe",
    tone: "metric",
    label: { ja: "ROE", en: "ROE" },
    tag: { ja: "指標", en: "Metric" },
    summary: {
      ja: "自己資本に対してどれだけ利益を出せているかを示す指標です。",
      en: "A profitability metric showing earnings relative to shareholders' equity.",
    },
    detail: {
      ja: "高いほど優秀に見えますが、本業の強さだけでなく借入の大きさでも押し上がります。だから ROIC やデュポン分解と合わせて中身を確かめます。",
      en: "It can be lifted by leverage, so it should be checked together with ROIC and DuPont decomposition.",
    },
    meaning: {
      ja: "見た目の高さだけで強い会社と決めず、利益率やレバレッジに分けて中身を読むべき指標です。",
      en: "A useful profitability metric whose drivers still need to be separated.",
    },
  },
  roic: {
    id: "roic",
    tone: "metric",
    label: { ja: "ROIC", en: "ROIC" },
    tag: { ja: "指標", en: "Metric" },
    summary: {
      ja: "事業に投じた資本をどれだけ効率よく使えているかを見る指標です。",
      en: "A metric for how efficiently invested capital is being used.",
    },
    detail: {
      ja: "ROE が高くても ROIC が弱いなら、本業の稼ぐ力より財務の見え方で良く見えている可能性があります。競合比較で中身を確かめるときの重要な軸です。",
      en: "A weaker ROIC next to a high ROE can suggest that leverage, not core operations, is driving appearances.",
    },
    meaning: {
      ja: "本業の資本効率を確かめるために使う指標で、ROE の中身を見分ける助けになります。",
      en: "A capital-efficiency metric that helps separate operational strength from leverage effects.",
    },
  },
  "dupont-analysis": {
    id: "dupont-analysis",
    tone: "model",
    label: { ja: "デュポン分解", en: "DuPont Analysis" },
    tag: { ja: "モデル", en: "Model" },
    summary: {
      ja: "ROE を利益率、資産回転率、財務レバレッジに分けて考える見方です。",
      en: "A framework that decomposes ROE into margin, asset turnover, and leverage.",
    },
    detail: {
      ja: "ROE の高さが本業の強さから来ているのか、借入の効きで押し上がっているのかを切り分けるために使います。競合比較で ROE をそのまま受け取らない理由がここにあります。",
      en: "It helps separate ROE driven by strong operations from ROE boosted mainly by leverage.",
    },
    meaning: {
      ja: "ROE の高さを3つの部品に分けて、その高さに中身があるかを確かめるための分解法です。",
      en: "A decomposition used to understand what is really driving ROE.",
    },
  },
  "financial-leverage": {
    id: "financial-leverage",
    tone: "risk",
    label: { ja: "財務レバレッジ", en: "Financial Leverage" },
    tag: { ja: "リスク", en: "Risk" },
    summary: {
      ja: "借入を使って資本効率を押し上げている度合いです。",
      en: "A measure of how much leverage is being used to amplify returns on equity.",
    },
    detail: {
      ja: "大きすぎると、ROE が高く見えても本業の強さではなく借入の効きで押し上がっている可能性があります。景気が悪くなると逆回転しやすい点にも注意が必要です。",
      en: "Too much leverage can inflate ROE and make downturns more dangerous.",
    },
    meaning: {
      ja: "ROE の見え方を良くする一方で、下振れ時の弱さも強めるため、競合比較で必ず確かめたい項目です。",
      en: "A leverage measure that can both lift ROE and increase downside fragility.",
    },
  },
  "debt-equity-ratio": {
    id: "debt-equity-ratio",
    tone: "risk",
    label: { ja: "負債資本倍率", en: "Debt-to-Equity Ratio" },
    tag: { ja: "リスク", en: "Risk" },
    summary: {
      ja: "自己資本に対して借入がどれくらいあるかを見る数字です。",
      en: "A ratio comparing debt to equity.",
    },
    detail: {
      ja: "同業より大きいなら、利益が少し崩れただけでも財務の余裕が縮みやすくなります。割安に見える理由の1つになっていることも多い項目です。",
      en: "A high ratio can make even modest earnings weakness more problematic and may explain a low valuation.",
    },
    meaning: {
      ja: "借入の重さを確認して、見た目の割安さの裏に財務負担がないかを確かめるための指標です。",
      en: "A leverage burden metric used to check whether valuation discounts reflect financial strain.",
    },
  },
  "expected-return": {
    id: "expected-return",
    tone: "metric",
    label: { ja: "期待リターン", en: "Expected Return" },
    tag: { ja: "指標", en: "Metric" },
    summary: {
      ja: "今の配分で年あたりどれくらい増えそうかを見積もる指標です。",
      en: "A portfolio-level estimate of how much it may grow per year.",
    },
    detail: {
      ja: "伸びの大きさをつかむ入口ですが、高いだけでは持ちやすさは分かりません。リスクや集中度と並べて初めて全体像が見えます。",
      en: "It is a starting point for growth, but it must be compared with risk and concentration.",
    },
    meaning: {
      ja: "組み合わせ全体の伸びやすさを見る最初の数字で、リスクとセットで読む前提の指標です。",
      en: "A starting-point metric for portfolio growth potential.",
    },
  },
  "portfolio-dividend-yield": {
    id: "portfolio-dividend-yield",
    tone: "metric",
    label: { ja: "ポートフォリオ配当利回り", en: "Portfolio Dividend Yield" },
    tag: { ja: "指標", en: "Metric" },
    summary: {
      ja: "組み合わせ全体でどれくらい配当が取れるかを見る数字です。",
      en: "A portfolio-level measure of dividend yield across the whole allocation.",
    },
    detail: {
      ja: "高配当銘柄を1つ持っていても、比率が小さければ全体にはあまり効きません。保有比率込みで、実際にどれだけ利回りへ寄与しているかを見るために使います。",
      en: "A single high-yield holding may matter little if its weight is small, so the full allocation has to be considered.",
    },
    meaning: {
      ja: "個別銘柄の利回りではなく、保有比率込みで全体の配当がどこまで効いているかを見る指標です。",
      en: "A yield measure that reflects allocation weights, not just single-stock yields.",
    },
  },
  "effective-holdings": {
    id: "effective-holdings",
    tone: "data",
    label: { ja: "実効銘柄数", en: "Effective Holdings" },
    tag: { ja: "データ", en: "Data" },
    summary: {
      ja: "見た目ではなく、実際にどれくらい分散できているかを表す数字です。",
      en: "A number showing how diversified a portfolio effectively is, beyond the headline holding count.",
    },
    detail: {
      ja: "8 銘柄持っていても一部に偏っていれば、この数字はもっと小さくなります。保有数の多さではなく、実態として何銘柄分くらい分散できているかを見るために使います。",
      en: "A portfolio can hold many names but still behave like a much smaller set if weights are concentrated.",
    },
    meaning: {
      ja: "見た目の保有数と実際の分散度の差を測るための数字で、偏りの強さを数値でつかむのに役立ちます。",
      en: "A metric that reveals the gap between the visible number of holdings and true diversification.",
    },
  },
  "top3-concentration": {
    id: "top3-concentration",
    tone: "risk",
    label: { ja: "上位3銘柄集中度", en: "Top-3 Concentration" },
    tag: { ja: "リスク", en: "Risk" },
    summary: {
      ja: "上位の数銘柄にどれだけ依存しているかを見る数字です。",
      en: "A metric showing how much of the portfolio depends on the top three holdings.",
    },
    detail: {
      ja: "高いと、一部の主力銘柄が崩れたときにポートフォリオ全体も一緒に大きく動きやすくなります。銘柄数より先に見たい偏りの指標です。",
      en: "High concentration means a few positions can drive the whole portfolio.",
    },
    meaning: {
      ja: "分散しているつもりでも実際には数銘柄へ依存していないかを確かめるための集中度指標です。",
      en: "A concentration metric for checking whether a portfolio is overly dependent on a few holdings.",
    },
  },
  var: {
    id: "var",
    tone: "risk",
    label: { ja: "VaR", en: "VaR" },
    tag: { ja: "リスク", en: "Risk" },
    summary: {
      ja: "かなり悪い日にどれくらい下がるかの目安を示す指標です。",
      en: "A risk metric showing the approximate loss level for a very bad day.",
    },
    detail: {
      ja: "普段の揺れではなく、『まず覚悟しておきたい悪い日』の損失水準をつかむために使います。下方リスクでは境界線として読みます。",
      en: "It is used as a threshold for losses on unusually bad days.",
    },
    meaning: {
      ja: "悪い日を想定したとき、まずどこまでの下げを覚悟しておくべきかを見るための指標です。",
      en: "A boundary-loss metric for adverse days.",
    },
  },
  cvar: {
    id: "cvar",
    tone: "risk",
    label: { ja: "CVaR", en: "CVaR" },
    tag: { ja: "リスク", en: "Risk" },
    summary: {
      ja: "VaR を超えたさらに悪い日だけを集めた平均下落です。",
      en: "An average loss for days that are even worse than the VaR threshold.",
    },
    detail: {
      ja: "VaR より一段厳しい局面で、どこまで下げが深くなりうるかを示します。境界線の先にある重さを確認するために使います。",
      en: "It captures the heaviness of losses beyond the VaR boundary.",
    },
    meaning: {
      ja: "かなり悪い日よりさらに深い下げが起きたとき、その平均的な重さを確認するための指標です。",
      en: "A tail-loss metric for the severity beyond VaR.",
    },
  },
  "crisis-scenario": {
    id: "crisis-scenario",
    tone: "risk",
    label: { ja: "危機シナリオ", en: "Crisis Scenario" },
    tag: { ja: "リスク", en: "Risk" },
    summary: {
      ja: "過去の危機相場を今の保有比率に当てはめたときの想定損益です。",
      en: "A hypothetical portfolio outcome if a past crisis-like move hit the current allocation.",
    },
    detail: {
      ja: "理論上の統計だけでなく、『同じことが起きたら自分の組み合わせはどうなるか』を具体的に想像しやすくするための項目です。",
      en: "It turns abstract risk into a concrete what-if using real crisis moves.",
    },
    meaning: {
      ja: "過去の荒い局面を当てはめて、統計ではなく実例ベースで下げの深さを確かめるためのシナリオです。",
      en: "A what-if scenario using real crisis moves rather than only statistical tails.",
    },
  },
  "diversification-score": {
    id: "diversification-score",
    tone: "data",
    label: { ja: "分散スコア", en: "Diversification Score" },
    tag: { ja: "データ", en: "Data" },
    summary: {
      ja: "候補が今の組み合わせの偏りをどれだけ和らげるかを表す評価です。",
      en: "A score for how much a candidate can reduce existing portfolio concentration.",
    },
    detail: {
      ja: "既存ポートフォリオとの相関の低さや、業種の重なりの少なさをまとめて見ます。単独で良い銘柄かではなく、いま足す意味があるかを判断するための点数です。",
      en: "It combines low correlation and low theme overlap to judge whether a candidate adds diversification value.",
    },
    meaning: {
      ja: "候補銘柄そのものの魅力ではなく、今の組み合わせに加える意味があるかを測るための評価です。",
      en: "A score for how useful a candidate is to the current portfolio mix.",
    },
  },
  "return-improvement": {
    id: "return-improvement",
    tone: "metric",
    label: { ja: "リターン改善", en: "Return Improvement" },
    tag: { ja: "指標", en: "Metric" },
    summary: {
      ja: "候補を足したときに期待リターンがどれだけ増えるかを見る数字です。",
      en: "A metric for how much expected return increases after adding a candidate.",
    },
    detail: {
      ja: "伸びを優先したいときに重く見る項目ですが、それだけで上位にすると既存の偏りを強めることもあります。改善量を見ても単独で採用判断は出しません。",
      en: "It matters most for growth-oriented ranking, but it cannot be used alone.",
    },
    meaning: {
      ja: "候補がどれだけ伸びやすさへ効くかを見る指標で、安定性や分散への影響と一緒に読む必要があります。",
      en: "A growth-impact metric that needs to be balanced against stability and diversification.",
    },
  },
  "risk-improvement": {
    id: "risk-improvement",
    tone: "metric",
    label: { ja: "リスク改善", en: "Risk Improvement" },
    tag: { ja: "指標", en: "Metric" },
    summary: {
      ja: "候補を足したときにどれだけ安定するかを見る数字です。",
      en: "A metric for how much overall portfolio risk declines after adding a candidate.",
    },
    detail: {
      ja: "値が大きいほど上下の揺れを抑える効果が期待できますが、リターン改善とのバランスで評価は変わります。組み替えではこの軸が独立して重要です。",
      en: "A stronger value means more stabilizing effect, but it must be weighed against return improvement.",
    },
    meaning: {
      ja: "候補がどれだけポートフォリオを持ちやすくするかを見る指標で、伸びとのトレードオフを考える材料になります。",
      en: "A stability-impact metric for judging how much easier a candidate makes the portfolio to hold.",
    },
  },
};

function resolveLocalizedText(
  value: LocalizedText | Partial<LocalizedText> | undefined,
  locale: Locale
): string | undefined {
  if (!value) return undefined;
  return value[locale] ?? value.ja;
}

function resolveDocTerm(definition: LocalizedDocTermDefinition, locale: Locale): DocTermDefinition {
  return {
    id: definition.id,
    tone: definition.tone,
    label: resolveLocalizedText(definition.label, locale) ?? definition.id,
    tag: resolveLocalizedText(definition.tag, locale) ?? "",
    summary: resolveLocalizedText(definition.summary, locale) ?? "",
    detail: resolveLocalizedText(definition.detail, locale),
    meaning: resolveLocalizedText(definition.meaning, locale) ?? "",
  };
}

export function getDocTerm(termId: DocTermId, locale: Locale): DocTermDefinition {
  return resolveDocTerm(DOC_TERM_DEFINITIONS[termId], locale);
}
